if (!customElements.get('nav-menu')) {
  class NavMenu extends HTMLElement {
    constructor() {
      super();
      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleHamburgerClick = this.handleHamburgerClick.bind(this);
      this.handleDrawerClose = this.handleDrawerClose.bind(this);
      this.handleOverlayClick = this.handleOverlayClick.bind(this);
    }

    connectedCallback() {
      this.container = this.querySelector('.nav-menu__container');
      this.drawerClose = this.querySelector('.nav-menu__drawer-close');
      this.overlay = this.querySelector('.nav-menu__overlay');
      this.tabs = this.querySelectorAll('.nav-menu__tab');
      this.panels = this.querySelectorAll('.nav-menu__panel');

      this.STORAGE_KEY = 'nav-menu:last-tab';
      this.COLLECTION_KEY = 'nav-menu:last-collection';

      if (this.dataset.clearLastCollection === 'true') {
        try {
          sessionStorage.removeItem(this.STORAGE_KEY);
          sessionStorage.removeItem(this.COLLECTION_KEY);
        } catch (_) {}
      }

      const serverActive = this.dataset.serverActiveTab;
      if (serverActive != null) {
        try { sessionStorage.setItem(this.STORAGE_KEY, serverActive); } catch (_) {}
      } else {
        this.restoreStoredTab();
      }

      const currentCollection = this.dataset.currentCollection;
      if (currentCollection) {
        try { sessionStorage.setItem(this.COLLECTION_KEY, currentCollection); } catch (_) {}
      } else {
        this.applyLastCollectionMark();
      }

      this.hamburger = document.querySelector('.header__icon--menu');

      if (this.hamburger) {
        this.hamburger.addEventListener('click', this.handleHamburgerClick);
      }

      if (this.drawerClose) {
        this.drawerClose.addEventListener('click', this.handleDrawerClose);
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', this.handleOverlayClick);
      }

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.handleTabClick);
      });

      this.initSubmenus();
      this.initBackButtons();

      const tabsContainer = this.querySelector('.nav-menu__tabs');
      if (tabsContainer) this.tabsDrag = theme.utils.initDragScroll(tabsContainer);

      document.addEventListener('keydown', this.handleKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
      this.tabsDrag?.destroy();

      if (this.hamburger) {
        this.hamburger.removeEventListener('click', this.handleHamburgerClick);
      }

      if (this.drawerClose) {
        this.drawerClose.removeEventListener('click', this.handleDrawerClose);
      }

      if (this.overlay) {
        this.overlay.removeEventListener('click', this.handleOverlayClick);
      }

      this.tabs?.forEach((tab) => {
        tab.removeEventListener('click', this.handleTabClick);
      });
    }

    handleHamburgerClick() {
      this.toggleDrawer();
    }

    handleDrawerClose() {
      this.closeDrawer();
    }

    handleOverlayClick() {
      this.closeDrawer();
    }

    toggleDrawer() {
      if (!this.hamburger) return;
      const isOpen = this.hamburger.getAttribute('aria-expanded') === 'true';
      isOpen ? this.closeDrawer() : this.openDrawer();
    }

    openDrawer() {
      if (this.hamburger) this.hamburger.setAttribute('aria-expanded', 'true');
      if (this.container) this.container.setAttribute('aria-hidden', 'false');
      if (this.overlay) this.overlay.setAttribute('aria-hidden', 'false');
      this.scrollPosition = window.scrollY;
      document.documentElement.style.setProperty('--nav-scroll-y', `-${this.scrollPosition}px`);
      document.body.classList.add('nav-menu-open');
      this.closeAllSubmenus();
      this.openActiveSubmenu();
      this.updateFocusableElements();
      requestAnimationFrame(() => {
        this.drawerClose?.focus();
      });
    }

    closeDrawer() {
      if (this.hamburger) this.hamburger.setAttribute('aria-expanded', 'false');
      if (this.container) this.container.setAttribute('aria-hidden', 'true');
      if (this.overlay) this.overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('nav-menu-open');
      document.documentElement.style.removeProperty('--nav-scroll-y');
      window.scrollTo(0, this.scrollPosition || 0);
      this.closeAllSubmenus();
      this.hamburger?.focus();
    }

    updateFocusableElements() {
      const focusableSelectors = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const elements = this.container?.querySelectorAll(focusableSelectors) || [];
      this.focusableElements = Array.from(elements).filter(el => el.offsetWidth > 0 && el.offsetHeight > 0);
    }

    handleTabClick(e) {
      if (this.tabsDrag?.hasMoved) return;
      const tabIndex = e.currentTarget.dataset.tabIndex;
      this.activateTab(tabIndex);
      try { sessionStorage.setItem(this.STORAGE_KEY, tabIndex); } catch (_) {}
    }

    activateTab(tabIndex) {
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.tabIndex === tabIndex;
        tab.classList.toggle('nav-menu__tab--active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.panelIndex === tabIndex;
        panel.classList.toggle('nav-menu__panel--active', isActive);
      });

      this.closeAllSubmenus();
    }

    restoreStoredTab() {
      let stored = null;
      try { stored = sessionStorage.getItem(this.STORAGE_KEY); } catch (_) {}
      if (stored == null) return;
      const exists = Array.from(this.tabs).some((t) => t.dataset.tabIndex === stored);
      if (!exists) return;
      this.activateTab(stored);
    }

    applyLastCollectionMark() {
      let handle = null;
      try { handle = sessionStorage.getItem(this.COLLECTION_KEY); } catch (_) {}
      if (!handle) return;
      const suffix = '/collections/' + handle;
      const link = Array.from(this.querySelectorAll('.nav-menu__link')).find((a) => {
        const href = a.getAttribute('href') || '';
        return href === suffix || href.endsWith(suffix);
      });
      if (!link) return;
      link.classList.add('is-active');
      link.setAttribute('aria-current', 'page');
      if (link.classList.contains('nav-menu__link--level-3')) {
        link.closest('.nav-menu__item--level-2')?.classList.add('nav-menu__item--has-active-child');
      }
    }

    initSubmenus() {
      this.querySelectorAll('.nav-menu__submenu-toggle').forEach((toggle) => {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openSubmenu(toggle.closest('.nav-menu__item'));
        });
      });

      this.querySelectorAll('.nav-menu__item--has-children .nav-menu__link--level-2').forEach((link) => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          this.openSubmenu(link.closest('.nav-menu__item'));
        });
      });
    }

    openSubmenu(item) {
      if (!item) return;
      const submenu = item.querySelector('.nav-menu__submenu');
      const toggle = item.querySelector('.nav-menu__submenu-toggle');
      const panelsWrapper = this.querySelector('.nav-menu__panels-wrapper');
      if (panelsWrapper && submenu) panelsWrapper.style.minHeight = submenu.scrollHeight + 'px';
      if (submenu) submenu.setAttribute('aria-hidden', 'false');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    }

    initBackButtons() {
      this.querySelectorAll('.nav-menu__back').forEach((button) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const submenu = button.closest('.nav-menu__submenu');
          if (submenu) {
            submenu.setAttribute('aria-hidden', 'true');
            const toggle = submenu.parentElement.querySelector('.nav-menu__submenu-toggle');
            if (toggle) toggle.setAttribute('aria-expanded', 'false');
          }
          const panelsWrapper = this.querySelector('.nav-menu__panels-wrapper');
          if (panelsWrapper) panelsWrapper.style.minHeight = '';
        });
      });
    }

    openActiveSubmenu() {
      const activePanel = this.querySelector('.nav-menu__panel--active');
      const activeItem = activePanel?.querySelector('.nav-menu__item--has-active-child');
      if (activeItem) this.openSubmenu(activeItem);
    }

    closeAllSubmenus() {
      this.querySelectorAll('.nav-menu__submenu').forEach((submenu) => {
        submenu.setAttribute('aria-hidden', 'true');
      });
      this.querySelectorAll('.nav-menu__submenu-toggle[aria-expanded="true"]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
      });
      const panelsWrapper = this.querySelector('.nav-menu__panels-wrapper');
      if (panelsWrapper) panelsWrapper.style.minHeight = '';
    }

    handleKeydown(e) {
      if (this.container?.getAttribute('aria-hidden') !== 'false') return;

      if (e.key === 'Escape') {
        this.closeDrawer();
        return;
      }

      if (e.key === 'Tab') {
        this.updateFocusableElements();
        const first = this.focusableElements?.[0];
        const last = this.focusableElements?.[this.focusableElements.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          last?.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first?.focus();
          e.preventDefault();
        }
      }
    }
  }

  customElements.define('nav-menu', NavMenu);
}
